import { DEFAULT_PROJECT_SORT, PROJECT_SORT_KEYS } from '@core/query/project-sort.js';
import { readStoredProjectsSort } from '@core/storage/preferences-mirror.js';
import { stripSearchParams, type SearchMiddleware } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * Search-param contract for the projects list route. Defaults are stripped
 * from the URL by `stripSearchParams` middleware on the route, so
 * `?sort=visited_desc` and `?q=` never appear in the address bar.
 */
const PROJECTS_SEARCH_DEFAULTS = { sort: DEFAULT_PROJECT_SORT, q: '' } as const;

export const projectsSearchSchema = z.object({
  // The sort fallback reads the mirrored preference: search middlewares only
  // run when building navigations, so the initial location (hard reload,
  // pasted URL) resolves its sort here instead.
  sort: z
    .enum(PROJECT_SORT_KEYS)
    .catch(() => readStoredProjectsSort() ?? PROJECTS_SEARCH_DEFAULTS.sort),
  q: z.string().catch(PROJECTS_SEARCH_DEFAULTS.q),
});

export type ProjectsSearch = z.infer<typeof projectsSearchSchema>;

/**
 * Keeps the URL canonical for the remembered sort: a navigation that names
 * no sort gets the mirrored preference injected, so the address bar shows
 * the sort that is actually rendered. Runs before `stripSearchParams`, which
 * then keeps the global default ('visited') out of the URL.
 *
 * MUST stay a pure read. Middlewares run on every location build — link
 * hrefs and intent preloads included — so a write here would let a stale
 * build (still carrying the previous sort) race and overwrite the user's
 * explicit choice. The write lives in the sort dropdown handler instead.
 */
export const injectStoredProjectsSort: SearchMiddleware<ProjectsSearch> = ({
  search,
  next,
}) => {
  if (search.sort === undefined) {
    const stored = readStoredProjectsSort();
    if (stored !== null && stored !== PROJECTS_SEARCH_DEFAULTS.sort) {
      return next({ ...search, sort: stored });
    }
  }
  return next(search);
};

/** The route's full middleware chain — shared with the unit-test harness. */
export const projectsSearchMiddlewares = [
  injectStoredProjectsSort,
  stripSearchParams(PROJECTS_SEARCH_DEFAULTS),
];
